import { useCallback, useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";

const Tag = ({ children, side }) => (
  <span
    className={`pointer-events-none absolute top-3 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] backdrop-blur ${
      side === "left" ? "left-3 bg-cs-navy/80 text-white" : "right-3 bg-cs-yellow/90 text-cs-navy"
    }`}
  >
    {children}
  </span>
);

export const CompareSlider = ({ before, after, alt, testId }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const update = useCallback((clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const onDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    update(e.clientX);
  };
  const onMove = (e) => dragging && update(e.clientX);
  const onUp = () => setDragging(false);
  const onKey = (e) => {
    const step = e.shiftKey ? 10 : 4;
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - step));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + step));
  };

  return (
    <div
      ref={ref}
      data-testid={testId}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      className={`relative aspect-[3/2] select-none overflow-hidden rounded-xl bg-cs-navy ${dragging ? "cursor-grabbing" : "cursor-ew-resize"}`}
      style={{ touchAction: "pan-y" }}
    >
      <img src={after} alt={`${alt} – après`} draggable={false} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt={`${alt} – avant`} draggable={false} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      </div>
      <Tag side="left">Avant</Tag>
      <Tag side="right">Après</Tag>
      <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.4)]" style={{ left: `${pos}%` }} />
      <div
        role="slider"
        tabIndex={0}
        aria-label={`Comparer avant / après : ${alt}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        onKeyDown={onKey}
        data-testid={`${testId}-handle`}
        className="absolute top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 border-white bg-cs-yellow text-cs-navy shadow-lg outline-none transition-transform duration-200 hover:scale-110 focus-visible:ring-4 focus-visible:ring-cs-yellow/50"
        style={{ left: `${pos}%` }}
      >
        <ChevronsLeftRight className="h-5 w-5" strokeWidth={2.5} />
      </div>
    </div>
  );
};
